import { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import {
  Truck, Undo2, Wallet, MessagesSquare, RefreshCw, ShieldAlert, Info, Lock, PlugZap,
  CheckCircle2, CircleDashed, ChevronRight, PackageSearch, BarChart3, EyeOff,
} from "lucide-react";
import { toast } from "sonner";

const PLATFORM_LABEL = { tiktok: "TikTok Shop", shopee: "Shopee" };

const MODULES = [
  { key: "fulfillment", label: "Fulfillment", icon: Truck, perm: "marketplace:fulfillment_view" },
  { key: "returns", label: "Returns", icon: Undo2, perm: "marketplace:returns_view" },
  { key: "finance", label: "Finance", icon: Wallet, perm: "marketplace:finance_view" },
  { key: "cs", label: "Customer Service", icon: MessagesSquare, perm: "marketplace:cs_view" },
];

const ANALYTICS_TAB = { key: "analytics", label: "Shop Performance", icon: BarChart3, perm: "marketplace:reports_view", analytics: true };
const TABS = [...MODULES, ANALYTICS_TAB];

const money = (m) => (m && m.amount != null ? `${m.amount} ${m.currency || ""}`.trim() : "—");
const ts = (v) => (v ? new Date(v).toLocaleString() : "—");

const COLUMNS = {
  fulfillment: [
    ["package_id", "Package"], ["order_id", "Order"], ["status", "Status"],
    ["tracking_number", "Tracking"], ["shipping_provider", "Carrier"], ["store", "Store"],
  ],
  returns: [
    ["return_id", "Return"], ["order_id", "Order"], ["return_status", "Status"],
    ["return_type", "Type"], ["refund", "Refund", money], ["store", "Store"],
  ],
  finance: [
    ["statement_id", "Statement"], ["payment_status", "Status"],
    ["settlement", "Settlement", money], ["net_sales", "Net sales", money],
    ["statement_time", "Released", ts], ["store", "Store"],
  ],
  cs: [
    ["conversation_id", "Conversation"], ["participant_role", "Role"],
    ["unread_count", "Unread"], ["latest_message_type", "Last msg type"],
    ["latest_message_time", "Last msg", ts], ["store", "Store"],
  ],
};

export default function MarketplaceErpReads() {
  const { can } = useAuth();
  const allowed = TABS.filter((m) => can(m.perm));
  const [tab, setTab] = useState(allowed[0]?.key || "fulfillment");

  return (
    <div className="space-y-4" data-testid="marketplace-erp-page">
      <div>
        <div className="text-xs uppercase tracking-wide text-slate-400">Marketplace</div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><PlugZap className="w-6 h-6 text-indigo-500" />ERP Reads</h1>
        <p className="text-sm text-slate-500 mt-0.5">Read-only adapters · Malaysia (MY) market · no ship/label, no refund decisions, no payouts, no message sends. Only connected stores are shown.</p>
      </div>

      <div className="flex gap-1 border-b border-slate-200 overflow-x-auto" data-testid="erp-tabs">
        {TABS.map((m) => {
          const Icon = m.icon;
          const active = tab === m.key;
          const locked = !can(m.perm);
          return (
            <button
              key={m.key}
              data-testid={`erp-tab-${m.key}`}
              disabled={locked}
              onClick={() => setTab(m.key)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${
                active ? "border-indigo-500 text-indigo-600" : "border-transparent text-slate-500 hover:text-slate-800"
              } ${locked ? "opacity-40 cursor-not-allowed" : ""}`}
            >
              <Icon className="w-4 h-4" />{m.label}{locked && <Lock className="w-3 h-3" />}
            </button>
          );
        })}
      </div>

      {(() => {
        const t = TABS.find((m) => m.key === tab);
        if (!t || !can(t.perm)) return <div className="p-8 text-center text-slate-400" data-testid="erp-no-perm">You do not have permission to view this module.</div>;
        return t.analytics ? <ShopPerformanceView key="analytics" /> : <ModuleView key={tab} module={tab} />;
      })()}
    </div>
  );
}

// ========================= Shop Performance Analytics (TikTok 202609, read-only) =========================
const SP_COUNTS = [
  ["orders_count", "Orders"], ["items_sold", "Items sold"], ["sku_orders_count", "SKU orders"],
  ["customers_count", "Customers"], ["cancellations_and_returns", "Cancel / returns"],
];
const SP_TRAFFIC = [
  ["avg_visitors", "Visitors"], ["avg_page_views", "Page views"], ["product_clicks", "Product clicks"],
  ["product_impressions", "Impressions"], ["unique_clicks", "Unique clicks"],
];
const SP_MONEY = [
  ["gmv", "GMV"], ["refunds", "Refunds"], ["avg_order_value", "Avg order value"],
  ["shipping_fees", "Shipping fees"], ["tax", "Tax"],
];

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };
const num = (v) => (v == null ? "—" : String(v));

function MetricGrid({ pairs, obj, isMoney }) {
  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
      {pairs.map(([k, l]) => (
        <div key={k} className="flex justify-between gap-2">
          <dt className="text-slate-400">{l}</dt>
          <dd className="text-slate-800 font-medium" data-testid={`sp-metric-${k}`}>{isMoney ? money(obj?.[k]) : num(obj?.[k])}</dd>
        </div>
      ))}
    </dl>
  );
}

function PerfCard({ title, iv }) {
  const hasMoney = iv && iv.sales_money;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid={`sp-card-${title}`}>
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-slate-700">{title}</div>
        <span className="text-[10px] text-slate-400">{iv.start_date || "—"} → {iv.end_date || "—"}</span>
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Sales counts</div>
        <MetricGrid pairs={SP_COUNTS} obj={iv.counts} />
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Traffic</div>
        <MetricGrid pairs={SP_TRAFFIC} obj={iv.traffic} />
        <div className="text-[11px] text-slate-500 mt-1">Conversion rate: {iv.traffic?.avg_conversation_rate ?? "—"}</div>
      </div>
      {hasMoney ? (
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Sales (money)</div>
          <MetricGrid pairs={SP_MONEY} obj={iv.sales_money} isMoney />
        </div>
      ) : (
        <div className="flex items-center gap-1.5 text-[11px] text-slate-400 border-t pt-2" data-testid="sp-card-money-masked">
          <EyeOff className="w-3 h-3" />Financial figures hidden — requires Finance view permission
        </div>
      )}
    </div>
  );
}

function ShopPerformanceView() {
  const [stores, setStores] = useState([]);
  const [shopId, setShopId] = useState("");
  const [start, setStart] = useState(daysAgoISO(7));
  const [end, setEnd] = useState(todayISO());
  const [gran, setGran] = useState("ALL");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/marketplace/shop-performance/stores")
      .then((r) => {
        const s = r.data.stores || [];
        setStores(s);
        if (s.length) setShopId(s[0].value);
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);

  const load = async () => {
    if (!shopId) { toast.warning("Select a connected TikTok store first."); return; }
    setLoading(true);
    try {
      const r = await api.get("/marketplace/shop-performance", {
        params: { platform: "tiktok", shop_id: shopId, start_date_ge: start, end_date_lt: end, granularity: gran, currency: "LOCAL" },
      });
      setData(r.data);
      if (r.data.status !== "ok") toast.warning(r.data.reason || r.data.status);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const intervals = data?.status === "ok" ? (data.performance?.intervals || []) : [];

  return (
    <div className="space-y-4" data-testid="sp-view">
      <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold flex items-center gap-2"><BarChart3 className="w-4 h-4 text-indigo-500" />TikTok Shop Performance</div>
          <span className="text-[11px] text-slate-400 flex items-center gap-1"><Info className="w-3 h-3" />Read-only · v202609 · shop-timezone dates · end date exclusive</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <label className="text-xs text-slate-500 space-y-1">
            <span>Store (TikTok)</span>
            <select data-testid="sp-store-select" value={shopId} onChange={(e) => setShopId(e.target.value)} className="w-full h-9 rounded-md border border-slate-200 text-sm px-2">
              {stores.length === 0 && <option value="">No connected store</option>}
              {stores.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </label>
          <label className="text-xs text-slate-500 space-y-1">
            <span>Start (inclusive)</span>
            <input type="date" data-testid="sp-start-date" value={start} onChange={(e) => setStart(e.target.value)} className="w-full h-9 rounded-md border border-slate-200 text-sm px-2" />
          </label>
          <label className="text-xs text-slate-500 space-y-1">
            <span>End (exclusive)</span>
            <input type="date" data-testid="sp-end-date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-full h-9 rounded-md border border-slate-200 text-sm px-2" />
          </label>
          <label className="text-xs text-slate-500 space-y-1">
            <span>Granularity</span>
            <div className="flex gap-1">
              {["ALL", "1D"].map((g) => (
                <button key={g} data-testid={`sp-gran-${g.toLowerCase()}`} onClick={() => setGran(g)}
                  className={`flex-1 h-9 rounded-md border text-sm font-medium transition-colors ${gran === g ? "border-indigo-500 text-indigo-600 bg-indigo-50" : "border-slate-200 text-slate-500 hover:text-slate-800"}`}>
                  {g === "ALL" ? "Aggregate" : "Daily"}
                </button>
              ))}
            </div>
          </label>
          <label className="text-xs text-slate-500 space-y-1">
            <span>&nbsp;</span>
            <Button data-testid="sp-load-btn" onClick={load} disabled={loading || !shopId} className="w-full h-9">
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />Load
            </Button>
          </label>
        </div>
      </div>

      {data?.status === "ok" && data.finance_masked && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 flex items-center gap-1.5" data-testid="sp-masked-banner">
          <EyeOff className="w-3.5 h-3.5" />Financial figures are hidden for your role. Non-financial sales counts and traffic are shown. Requires the Finance view permission.
        </div>
      )}

      {data && data.status !== "ok" && (
        <div className="rounded-lg border border-slate-200 bg-white p-6 text-center text-sm text-slate-400" data-testid="sp-status">
          {data.reason || data.status}
        </div>
      )}

      {data?.status === "ok" && (
        <div className="space-y-3" data-testid="sp-results">
          <div className="text-[11px] text-slate-400">Latest available date: {data.performance?.latest_available_date || "—"} · granularity {data.granularity} · currency {data.currency}</div>
          {intervals.length === 0 ? (
            <div className="rounded-lg border border-slate-200 bg-white p-6 text-center text-sm text-slate-400" data-testid="sp-empty">No performance data for this period.</div>
          ) : gran === "ALL" ? (
            <PerfCard title="Aggregate" iv={intervals[0]} />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {intervals.map((iv, i) => <PerfCard key={iv.start_date || i} title={iv.start_date || `Day ${i + 1}`} iv={iv} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StateBadges({ s }) {
  // Honest separation: code/contract readiness vs seller-grant/app-approval vs actual live verification.
  const blocked = s.state === "blocked";
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid={`erp-support-${s.platform}`}>
      <span className="text-xs font-semibold text-slate-700">{PLATFORM_LABEL[s.platform]}</span>
      {blocked ? (
        <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-800 bg-amber-50" data-testid={`erp-state-${s.platform}`}>
          <ShieldAlert className="w-3 h-3 mr-1" />Blocked · grant required
        </Badge>
      ) : (
        <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-700 bg-emerald-50" data-testid={`erp-state-${s.platform}`}>
          <CheckCircle2 className="w-3 h-3 mr-1" />Contract-ready
        </Badge>
      )}
      {!blocked && (
        s.live_verified ? (
          <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-700 bg-emerald-50" data-testid={`erp-liveverified-${s.platform}`}>
            <CheckCircle2 className="w-3 h-3 mr-1" />Live-verified
          </Badge>
        ) : (
          <Badge variant="outline" className="text-[10px] border-slate-300 text-slate-500" data-testid={`erp-liveverified-${s.platform}`}>
            <CircleDashed className="w-3 h-3 mr-1" />Not live-verified
          </Badge>
        )
      )}
    </div>
  );
}

function ModuleView({ module }) {
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState("");
  const [drawer, setDrawer] = useState(null); // {platform, shopId, recordId, label}
  const cols = COLUMNS[module];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/marketplace/${module}`);
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [module]);

  useEffect(() => { load(); }, [load]);

  const sync = async (platform, shopId, label) => {
    const key = `${platform}:${shopId}`;
    setSyncing(key);
    try {
      const r = await api.post(`/marketplace/${module}/sync`, { platform, shop_id: shopId });
      const s = r.data.status;
      if (s === "blocked" || s === "unsupported") toast.warning(`${label}: ${r.data.reason}`);
      else if (s === "completed") toast.success(`${label}: synced ${r.data.records_ingested} new record(s)`);
      else if (s === "partial") toast.warning(`${label}: partial sync (${r.data.records_ingested}) — resumable`);
      else toast.error(`${label}: ${s}`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSyncing(""); }
  };

  if (loading) return <div className="p-8 text-center text-slate-400" data-testid="erp-loading">Loading…</div>;
  if (!data) return null;

  const support = data.support || [];
  const stores = data.stores || [];
  const anyAvailable = support.some((s) => s.available);
  const detailPerm = data.detail_perm;
  const idField = data.id_field;
  const canDetail = !!detailPerm && can(detailPerm);
  const detailPlatforms = new Set(support.filter((s) => s.detail_available).map((s) => s.platform));
  const rowClickable = (row) => canDetail && detailPlatforms.has(row.platform);

  return (
    <div className="space-y-4">
      {/* Platform availability / honest states */}
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="erp-support">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-semibold">{data.label}</div>
          <span className="text-[11px] text-slate-400 flex items-center gap-1">
            <Info className="w-3 h-3" /> Contract-ready = code proven by synthetic tests · Live-verified = exercised against a real connected store
          </span>
        </div>
        <div className="flex flex-wrap gap-4">
          {support.map((s) => <StateBadges key={s.platform} s={s} />)}
        </div>
        {support.filter((s) => s.state === "blocked").map((s) => (
          <p key={s.platform} className="text-[11px] text-amber-700 mt-2" data-testid={`erp-blocked-reason-${s.platform}`}>
            <span className="font-medium">{PLATFORM_LABEL[s.platform]}:</span> {s.blocked_reason}
          </p>
        ))}
      </div>

      {/* Per-store read-only sync triggers */}
      {anyAvailable && (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="erp-stores">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold">Connected stores</div>
            <span className="text-[11px] text-slate-400">Operator-triggered read-only ingestion</span>
          </div>
          {stores.length === 0 ? (
            <div className="text-xs text-slate-400 py-2" data-testid="erp-no-stores">No connected store. Connect a store in Marketplace Setup to ingest {data.label.toLowerCase()}.</div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {stores.map((st) => {
                const sup = support.find((s) => s.platform === st.platform);
                const canSync = sup?.available;
                const key = `${st.platform}:${st.value}`;
                return (
                  <Button
                    key={key}
                    variant="outline"
                    size="sm"
                    disabled={!canSync || syncing === key}
                    onClick={() => sync(st.platform, st.value, st.label)}
                    data-testid={`erp-sync-${st.platform}-${st.value}`}
                    className="h-8"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${syncing === key ? "animate-spin" : ""}`} />
                    {st.label} <span className="text-[10px] text-slate-400 ml-1 capitalize">({st.platform})</span>
                  </Button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Rows */}
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="erp-rows">
        {data.rows.length === 0 ? (
          <div className="text-xs text-slate-400 py-6 text-center" data-testid="erp-empty">
            {anyAvailable
              ? (stores.length === 0
                  ? "Adapter is contract-ready, but no store is connected. Connect a store in Marketplace Setup to ingest, then records appear here."
                  : "No records yet. Use a store button above to run a read-only sync.")
              : "Adapter blocked — nothing to display."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] text-slate-400 border-b">
                  {cols.map(([k, l]) => <th key={k} className="py-1.5 pr-3">{l}</th>)}
                  <th className="py-1.5 pr-3">Synced</th>
                  {canDetail && <th className="py-1.5" />}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row, i) => {
                  const clickable = rowClickable(row);
                  return (
                    <tr
                      key={row.id || i}
                      className={`border-b last:border-0 ${clickable ? "cursor-pointer hover:bg-slate-50 transition-colors" : ""}`}
                      data-testid={`erp-row-${i}`}
                      onClick={clickable ? () => setDrawer({
                        platform: row.platform, shopId: row.shop_id,
                        recordId: row[idField], label: row[idField],
                      }) : undefined}
                    >
                      {cols.map(([k, l, fmt]) => (
                        <td key={k} className="py-1.5 pr-3">{fmt ? fmt(row[k]) : (row[k] ?? "—")}</td>
                      ))}
                      <td className="py-1.5 pr-3 text-slate-400 text-[11px]">{ts(row.synced_at)}</td>
                      {canDetail && (
                        <td className="py-1.5 text-right">
                          {clickable && <ChevronRight className="w-4 h-4 text-slate-300 inline" data-testid={`erp-row-open-${i}`} />}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ManifestPanel module={module} />

      <DetailDrawer module={module} target={drawer} onClose={() => setDrawer(null)} />
    </div>
  );
}

function DetailDrawer({ module, target, onClose }) {
  const [state, setState] = useState({ loading: false, error: "", res: null });
  const [refreshing, setRefreshing] = useState(false);

  const fetchDetail = useCallback(async (refresh) => {
    if (!target) return;
    const { platform, shopId, recordId } = target;
    if (refresh) setRefreshing(true); else setState({ loading: true, error: "", res: null });
    try {
      const r = await api.get(
        `/marketplace/${module}/${platform}/${encodeURIComponent(shopId)}/${encodeURIComponent(recordId)}`,
        { params: refresh ? { refresh: true } : {} });
      if (r.data.status !== "ok") {
        setState({ loading: false, error: r.data.reason || r.data.status, res: null });
      } else {
        setState({ loading: false, error: "", res: r.data });
      }
    } catch (e) {
      setState({ loading: false, error: formatApiError(e.response?.data?.detail), res: null });
    } finally { setRefreshing(false); }
  }, [module, target]);

  useEffect(() => { if (target) fetchDetail(false); }, [target, fetchDetail]);

  const open = !!target;
  const res = state.res;

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="erp-detail-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <PackageSearch className="w-4 h-4 text-indigo-500" />
            {target?.label || "Detail"}
          </SheetTitle>
          <SheetDescription>
            {target && `${PLATFORM_LABEL[target.platform]} · store ${target.shopId}`}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          {res && (
            <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
              <div className="text-[11px] text-slate-500" data-testid="erp-detail-source">
                {res.source === "cached"
                  ? <>Showing <span className="font-semibold">ingested</span> record · last synced {ts(res.last_fetched)}</>
                  : <>Fetched live contract · {ts(res.last_fetched)}</>}
                <div className="text-slate-400">Not live-verified · read-only</div>
              </div>
              <Button size="sm" variant="outline" className="h-8" disabled={refreshing}
                onClick={() => fetchDetail(true)} data-testid="erp-detail-refresh">
                <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
                Refresh detail
              </Button>
            </div>
          )}

          {state.loading && <div className="py-10 text-center text-slate-400 text-sm" data-testid="erp-detail-loading">Loading…</div>}
          {!state.loading && state.error && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800" data-testid="erp-detail-error">
              {state.error}
            </div>
          )}

          {res && <RecordSummary summary={res.summary} module={module} />}
          {res && res.source === "cached" && !res.detail && (
            <p className="text-[11px] text-slate-400" data-testid="erp-detail-hint">
              Use <span className="font-medium">Refresh detail</span> to pull the full read contract (tracking events, line items, reverse-tracking) from the connected store.
            </p>
          )}
          {res && res.detail && <DetailBody module={module} platform={target.platform} detail={res.detail} />}
        </div>
      </SheetContent>
    </Sheet>
  );
}

const SKIP_KEYS = new Set(["platform", "shop_id", "store", "id", "synced_at", "updated_at"]);

function RecordSummary({ summary, module }) {
  if (!summary) return null;
  const fmt = (k, v) => {
    if (v && typeof v === "object" && "amount" in v) return money(v);
    if (/time$/.test(k) && v) return ts(v);
    return v ?? "—";
  };
  const entries = Object.entries(summary).filter(([k]) => !SKIP_KEYS.has(k));
  return (
    <div className="rounded-lg border border-slate-200 p-3" data-testid="erp-detail-summary">
      <div className="text-xs font-semibold mb-2 text-slate-600">Record</div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-xs">
        {entries.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-slate-400 capitalize">{k.replace(/_/g, " ")}</dt>
            <dd className="text-slate-800 text-right break-all">{String(fmt(k, v))}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function Timeline({ events }) {
  if (!events?.length) return <p className="text-xs text-slate-400">No tracking events.</p>;
  return (
    <ol className="relative border-l border-slate-200 ml-1 space-y-3" data-testid="erp-detail-timeline">
      {events.map((e, i) => (
        <li key={i} className="ml-3">
          <span className="absolute -left-[5px] w-2.5 h-2.5 rounded-full bg-indigo-400" />
          <div className="text-xs font-medium text-slate-700">{e.status || e.description || "—"}</div>
          {e.description && e.status && <div className="text-[11px] text-slate-500">{e.description}</div>}
          <div className="text-[11px] text-slate-400">{ts(e.time)}</div>
        </li>
      ))}
    </ol>
  );
}

function DetailBody({ module, platform, detail }) {
  if (module === "fulfillment") {
    return (
      <div className="rounded-lg border border-slate-200 p-3" data-testid="erp-detail-body">
        <div className="text-xs font-semibold mb-2 text-slate-600">Tracking events</div>
        <Timeline events={detail.tracking_events} />
      </div>
    );
  }
  if (module === "returns") {
    const rt = detail.reverse_tracking;
    return (
      <div className="space-y-3" data-testid="erp-detail-body">
        {Array.isArray(detail.records) && (
          <div className="rounded-lg border border-slate-200 p-3">
            <div className="text-xs font-semibold mb-2 text-slate-600">Return history ({detail.record_count})</div>
            <Timeline events={(detail.records || []).map((r) => ({ status: `${r.event || ""} ${r.status || ""}`.trim(), time: r.time }))} />
          </div>
        )}
        <div className="rounded-lg border border-slate-200 p-3">
          <div className="text-xs font-semibold mb-2 text-slate-600">Reverse tracking</div>
          {rt && (rt.tracking_number || rt.carrier) ? (
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-xs">
              <dt className="text-slate-400">Tracking #</dt><dd className="text-right text-slate-800">{rt.tracking_number || "—"}</dd>
              <dt className="text-slate-400">Carrier</dt><dd className="text-right text-slate-800">{rt.carrier || "—"}</dd>
              <dt className="text-slate-400">Status</dt><dd className="text-right text-slate-800">{rt.status || "—"}</dd>
            </dl>
          ) : <p className="text-[11px] text-slate-500">{rt?.note || "Reverse-tracking not available for this platform/contract."}</p>}
        </div>
      </div>
    );
  }
  if (module === "finance") {
    if (platform === "shopee") {
      return (
        <div className="rounded-lg border border-slate-200 p-3" data-testid="erp-detail-body">
          <div className="text-xs font-semibold mb-2 text-slate-600">Settlement breakdown</div>
          <table className="w-full text-xs">
            <tbody>
              {(detail.components || []).map((c) => (
                <tr key={c.key} className="border-b last:border-0">
                  <td className="py-1 text-slate-500">{c.label}</td>
                  <td className="py-1 text-right text-slate-800">{money(c.value)}</td>
                </tr>
              ))}
              <tr className="font-semibold"><td className="py-1">Payout</td><td className="py-1 text-right">{money(detail.payout)}</td></tr>
            </tbody>
          </table>
        </div>
      );
    }
    return (
      <div className="rounded-lg border border-slate-200 p-3" data-testid="erp-detail-body">
        <div className="text-xs font-semibold mb-2 text-slate-600">Statement transactions ({detail.transaction_count})</div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-left text-slate-400 border-b">
              <th className="py-1 pr-2">Type</th><th className="py-1 pr-2">Settlement</th><th className="py-1 pr-2">Fee</th><th className="py-1">When</th>
            </tr></thead>
            <tbody>
              {(detail.transactions || []).map((t, i) => (
                <tr key={i} className="border-b last:border-0">
                  <td className="py-1 pr-2 text-slate-600">{t.type || "—"}</td>
                  <td className="py-1 pr-2 text-slate-800">{money(t.settlement)}</td>
                  <td className="py-1 pr-2 text-slate-800">{money(t.fee)}</td>
                  <td className="py-1 text-slate-400">{ts(t.time)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
  return null;
}

function ManifestPanel({ module }) {
  const [man, setMan] = useState(null);
  useEffect(() => {
    api.get("/marketplace/erp-reads/manifest").then((r) => setMan(r.data)).catch(() => {});
  }, []);
  if (!man) return null;
  const mod = (man.modules || []).find((m) => m.module === module);
  if (!mod) return null;
  const verColor = (v = "") =>
    v.startsWith("code-ready") ? "border-emerald-300 text-emerald-700 bg-emerald-50"
    : v.startsWith("needs-repin") ? "border-rose-300 text-rose-700 bg-rose-50"
    : v.startsWith("blocked") ? "border-amber-300 text-amber-800 bg-amber-50"
    : "border-slate-300 text-slate-500";
  return (
    <details className="rounded-xl border border-slate-200 bg-white p-4" data-testid="erp-manifest">
      <summary className="cursor-pointer text-sm font-semibold flex items-center gap-2">
        <Info className="w-4 h-4 text-slate-400" />Endpoint manifest & evidence — {man.market} · {man.access} · writes {man.writes}
        <span className="text-[10px] font-normal text-slate-400 ml-auto">sources captured {man.capture_date}</span>
      </summary>
      <div className="mt-3 space-y-4">
        {mod.platforms.map((p) => (
          <div key={p.platform} className="text-[11px]" data-testid={`erp-manifest-${p.platform}`}>
            <div className="flex items-center flex-wrap gap-2">
              <span className="font-semibold text-slate-700">{PLATFORM_LABEL[p.platform]}</span>
              <Badge variant="outline" className="text-[10px]">{String(p.api_version).startsWith("v") ? p.api_version : `v${p.api_version}`}</Badge>
              <Badge variant="outline" className={`text-[10px] ${p.status === "blocked" ? "border-amber-300 text-amber-800 bg-amber-50" : "border-slate-300"}`}>{p.status}</Badge>
              <Badge variant="outline" className="text-[10px] border-slate-300 text-slate-500">{p.live_state}</Badge>
              <span className="text-slate-400">scopes: {(p.required_scopes || []).join(", ")}</span>
            </div>
            <div className="mt-1.5 ml-1 space-y-1.5">
              {p.endpoints.map((e) => (
                <div key={e.path} className="border-l-2 border-slate-100 pl-2" data-testid={`erp-manifest-ep-${e.operation}`}>
                  <div className="flex items-center flex-wrap gap-1.5">
                    <span className="font-mono text-slate-700">{e.method}</span>
                    <span className="font-mono text-slate-500 break-all">{e.path}</span>
                    {e.verification && <Badge variant="outline" className={`text-[9px] ${verColor(e.verification)}`}>{e.verification}</Badge>}
                  </div>
                  <div className="text-slate-400 mt-0.5">
                    scope: <span className="text-slate-500">{e.scope}</span>
                    {e.collection && <> · collection: <span className="text-slate-500">{e.collection}</span></>}
                    {e.id && <> · id: <span className="text-slate-500">{e.id}</span></>}
                  </div>
                  {e.pagination && <div className="text-slate-400">paging: <span className="text-slate-500">{e.pagination}</span></div>}
                  {e.window && <div className="text-slate-400">window: <span className="text-slate-500">{e.window}</span></div>}
                  {e.doc_url && <a href={e.doc_url} target="_blank" rel="noreferrer" className="text-indigo-500 hover:underline break-all" data-testid={`erp-manifest-doc-${e.operation}`}>official doc ↗</a>}
                </div>
              ))}
            </div>
            {p.blocked_reason && <p className="text-amber-700 mt-1.5">Blocked: {p.blocked_reason}</p>}
            {(p.contract_gaps || []).map((g, i) => <p key={i} className="text-rose-600 mt-1">Gap: {g}</p>)}
            {p.version_note && <p className="text-slate-400 mt-1">{p.version_note}</p>}
            {p.market_note && <p className="text-slate-400 mt-1">{p.market_note}</p>}
          </div>
        ))}
      </div>
    </details>
  );
}
